import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  supabase,
  onAuthStateChange,
  resetPassword,
  updatePassword,
  signOut,
} from '../services/supabase';
import logger from '../utils/logger';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Establish session from URL (email confirmation / magic link / recovery)
    // before reading the initial session to avoid race conditions that log the user out.
    const initializeAuthFromUrl = async () => {
      try {
        setLoading(true);

        // 1) If the URL contains auth tokens (hash), set the session explicitly
        const rawHash = typeof window !== 'undefined' ? window.location.hash || '' : '';
        const hashParams = new URLSearchParams(
          rawHash.startsWith('#') ? rawHash.slice(1) : rawHash
        );
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');

        if (accessToken && refreshToken) {
          await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          // Clean the URL to remove sensitive tokens but preserve the query string (e.g. ?type=signup)
          if (typeof window !== 'undefined') {
            window.history.replaceState(
              {},
              document.title,
              window.location.pathname + window.location.search
            );
          }
        }

        // 1b) If the URL contains a PKCE auth code (e.g., from email confirmation), exchange it
        if (typeof window !== 'undefined') {
          const currentUrl = new URL(window.location.href);
          const hasCode = currentUrl.searchParams.has('code');
          const hasError = currentUrl.searchParams.has('error_description');

          if (hasCode && !hasError) {
            try {
              // This parses the URL internally for code/state and stores the session
              await supabase.auth.exchangeCodeForSession(window.location.href);
              // Remove code/state from the URL, keep other params like type=signup
              currentUrl.searchParams.delete('code');
              currentUrl.searchParams.delete('state');
              window.history.replaceState(
                {},
                document.title,
                currentUrl.pathname +
                  (currentUrl.search ? `?${currentUrl.searchParams.toString()}` : '')
              );
            } catch (ex) {
              logger.warn('AuthContext: Code exchange failed or not applicable:', ex);
            }
          }
        }

        // 2) Read the current session after any potential setSession
        const {
          data: { session },
        } = await supabase.auth.getSession();
        setUser(session?.user ?? null);
      } catch (e) {
        logger.error('AuthContext: Error initializing session from URL:', e);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    initializeAuthFromUrl();

    // Listen for auth changes
    const {
      data: { subscription },
    } = onAuthStateChange((event, session) => {
      logger.debug('AuthContext: Auth state changed:', event, 'User:', session?.user?.id || 'null');
      setUser(session?.user ?? null);
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleForgotPassword = async (email) => {
    try {
      const { data, error } = await resetPassword(email);
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: error.message };
    }
  };

  const handleUpdatePassword = async (newPassword) => {
    try {
      const { data, error } = await updatePassword(newPassword);
      if (error) throw error;
      return { success: true, data };
    } catch (error) {
      console.error('Update password error:', error);
      return { success: false, error: error.message };
    }
  };

  const handleSignOut = async () => {
    try {
      logger.debug('AuthContext: Starting sign out process...');
      const { error } = await signOut();
      if (error) {
        const message = typeof error === 'string' ? error : error.message || '';
        const status = error.status || error.statusCode;
        if (/Auth session missing/i.test(message) || status === 401 || status === 403) {
          logger.warn(
            'AuthContext: Ignoring signOut error due to missing/invalid session, proceeding locally.'
          );
        } else {
          throw error;
        }
      }
      logger.debug('AuthContext: SignOut complete (remote or local), setting user to null...');
      setUser(null);
      logger.debug('AuthContext: User state updated, logout complete');
      return { success: true };
    } catch (error) {
      logger.error('Sign out error:', error);
      // Ensure UI still reflects logged-out state
      setUser(null);
      return { success: false, error: error.message };
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated: !!user,
    forgotPassword: handleForgotPassword,
    updatePassword: handleUpdatePassword,
    signOut: handleSignOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
